import * as React from "react";
import { useState, useRef } from "react";
import Header from "./Header";
import { makeStyles, Button as FluentButton } from "@fluentui/react-components";
import ReportsTree from './ReportsTree';
import ReportParams from './ReportParams';
import { TabList, Tab } from "@fluentui/react-tabs";
import { ArrowClockwise20Regular as RefreshIcon } from "@fluentui/react-icons";
import AiAgent from "./AiAgent";
import { config } from "../../config";

interface AppProps {
  title: string;
}

// Default task pane width (used when not in AI Assist mode)
const DEFAULT_TASKPANE_WIDTH = 350;
// Expanded task pane width for AI Assist mode (40% of window or fixed value)
const AI_ASSIST_TASKPANE_WIDTH = 1000;

const useStyles = makeStyles({
  root: {
    height: "100vh",
    overflow: "hidden",
    fontFamily: "Arial regular",
    fontSize: "12px",
    color: "#6D6E71",
  },
});

const App: React.FC<AppProps> = (props: AppProps) => {
  const styles = useStyles();

  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isBmxReport, setIsBmxReport] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [authToken, setAuthToken] = useState<string>("");
  const [identityProvider, setIdentityProvider] = useState<string>("Entra");
  const [oktaIdToken, setOktaIdToken] = useState<string>("");
  const [userID, setUserID] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const [forceAnalyzers, setForceAnalyzers] = useState<boolean>(false);
  const [shouldRefresh, setShouldRefresh] = useState<Date>(new Date());
  const [aiAgentActive, setAiAgentActive] = useState<boolean>(false);
  const [aiAgentDisabled, setAiAgentDisabled] = useState<boolean>(false);

  // Store original width to restore later
  const originalWidthRef = useRef<number | null>(null);

  /**
   * Resizes the task pane to the specified width using the TaskPaneAPI.
   * This API is available in Excel, PowerPoint, and Word on Windows/Mac with recent versions.
   * Falls back gracefully if the API is not available.
   */
  function setTaskPaneWidth(width: number): void {
    try {
      // Check if the TaskPaneAPI 1.1 requirement set is supported before attempting to use it
      if (Office.context.requirements && Office.context.requirements.isSetSupported("TaskPaneApi", "1.1")) {
        const officeAny = Office as any;
        if (officeAny.extensionLifeCycle && 
            officeAny.extensionLifeCycle.taskpane && 
            typeof officeAny.extensionLifeCycle.taskpane.setWidth === "function") {
          officeAny.extensionLifeCycle.taskpane.setWidth(width);
        }
      }
    } catch (error) {
      // Silently fail if API is not available - the add-in will still work with default width
    }
  }

  /**
   * Handles the AI Assist tab activation/deactivation.
   * Expands the task pane when AI Assist is active, restores original width when leaving.
   */
  function handleAiAgentActivation(isActive: boolean): void {
    if (isActive) {
      // Store current width before expanding (if we can determine it)
      if (originalWidthRef.current === null) {
        originalWidthRef.current = DEFAULT_TASKPANE_WIDTH;
      }
      setTaskPaneWidth(AI_ASSIST_TASKPANE_WIDTH);
    } else {
      // Restore to original/default width
      setTaskPaneWidth(originalWidthRef.current ?? DEFAULT_TASKPANE_WIDTH);
    }
    setAiAgentActive(isActive);
  }

  function userChanged(token: string, oktaIdToken: string, ID: string, name: string, identityProvider: string = "Entra") {
    setAuthToken(token);
    setOktaIdToken(oktaIdToken);
    setUserID(ID);
    setUserName(name);
    setIdentityProvider(identityProvider);
    setIsLoading(false);
    setAiAgentDisabled(isExternalUser(ID));
  }

  function isExternalUser(userID: string): boolean {
    const localDomains = [
      "constellation-builders.com",
      "constellationhb.com",
      "tss-yonder.com",
    ];
    const userDomain = userID.split("@")[1]?.toLowerCase() || "";
    return !localDomains.includes(userDomain);
  }

  function userRemoved() {
    userChanged("", null, "", "");
  }

  function handleRefresh() {
    setShouldRefresh(new Date());
  }

  Office.onReady(() => {
    Excel.run(async (context) => {
      try {
        let sheet = context.workbook.worksheets.getItemOrNullObject("(configuration)");
        sheet.load("name");
        await context.sync();
        if (!sheet.isNullObject) {
          setIsBmxReport(true);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        return context.sync();
      }
    });
  });

  return (
    <div className={styles.root}>
      <Header
        logo="assets/logo-large.png"
        title={props.title}
        isInitialized={isInitialized}
        oktaIdToken={oktaIdToken}
        userID={userID}
        userName={userName}
        identityProvider={identityProvider}
        setIsInitialized={setIsInitialized}
        setIsLoading={setIsLoading}
        userChanged={userChanged}
        userRemoved={userRemoved}
      />
      {userID ? (
        <section style={{ padding: "0 8px", marginBottom: "8px", borderBottom: "1px solid #ccc", display: "flex", alignItems: "center" }}>
          <TabList defaultSelectedValue={isBmxReport ? "data" : "analyzers"} onTabSelect={(_, tab) => { 
            setForceAnalyzers(tab.value == "analyzers");
            handleAiAgentActivation(tab.value == "ai");
            }}>
            <Tab value="analyzers">Analyzers</Tab>
            { !isBmxReport ? (
              <Tab value="data" disabled>Data</Tab>
            ) : (
              <Tab value="data">Data</Tab>
            )}
            {/* { !aiAgentDisabled && 
              <Tab value="ai">AI Assist</Tab>
            } */}
            <Tab value="ai">Stella AI</Tab>
          </TabList>
          <div style={{ flexGrow: 1 }} />
          { (!isBmxReport || forceAnalyzers) && !aiAgentActive &&
            <FluentButton
              appearance="subtle"
              icon={<RefreshIcon />}
              onClick={() => handleRefresh()}
            />}
        </section>) : null}
      <div style={{ padding: aiAgentActive ? "0" : "10px 20px", height: aiAgentActive ? "calc(100vh - 100px)" : "auto", overflow: "hidden" }}>
        { isLoading ? (
          <p>Please wait…</p>
          ) : (!userID ? (
          <div>
            <h3>Welcome to BuilderMetrix® for Excel!</h3>
            <ul>
              <li>This tool can be used to get data from BuilderMetrix® system.</li>
              <li>To get started, sign in from the top right button.</li>
            </ul>
          </div>
        ) : aiAgentActive ? (
      
          <iframe 
            src={config.stellaAiUrl || "https://demo.gostella.ai"} 
            data-stella-agent 
            data-stella-allowed-origins={JSON.stringify(config.stellaAiAllowedOrigins)}
            style={{
              width: "100%", 
              height: "calc(100vh - 120px)", 
              border: "none",
              display: "block"
            }}
          ></iframe>
        ) : (!isBmxReport || forceAnalyzers ? (
          <ReportsTree authToken={authToken} identityProvider={identityProvider} shouldRefresh={shouldRefresh} />
        ) : (
          <ReportParams authToken={authToken} identityProvider={identityProvider} shouldRefresh={shouldRefresh} />
        ))) }
      </div>
    </div>
  );
};

export default App;
